import type { CSSProperties } from 'react';

interface Props {
  /** Number of skeleton rows */
  rows?: number;
  /** Column count — determines skeleton cell widths */
  columns?: number;
  /** Optional column widths as percentages (must sum to ~100). If omitted, evenly distributed. */
  columnWidths?: number[];
}

/**
 * Loading skeleton for tables. Shows a header row + N body rows with shimmer animation.
 * Use instead of "불러오는 중…" text for tables while data is fetching.
 */
export function TableSkeleton({ rows = 5, columns = 4, columnWidths }: Props) {
  const widths = columnWidths ?? Array.from({ length: columns }, () => 100 / columns);
  return (
    <div role="status" aria-label="불러오는 중" style={{ width: '100%' }}>
      <div style={headerRow}>
        {widths.map((w, i) => (
          <div key={i} style={{ ...cell, width: `${w}%` }}>
            <div style={shimmer} />
          </div>
        ))}
      </div>
      {Array.from({ length: rows }, (_, ri) => (
        <div key={ri} style={bodyRow}>
          {widths.map((w, ci) => (
            <div key={ci} style={{ ...cell, width: `${w}%` }}>
              <div style={{ ...shimmer, width: `${60 + ((ri * 7 + ci * 11) % 30)}%` }} />
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

const headerRow: CSSProperties = {
  display: 'flex',
  background: 'var(--bg-sunken)',
  borderBottom: '1px solid var(--border-subtle)',
  padding: '8px 0',
};

const bodyRow: CSSProperties = {
  display: 'flex',
  borderBottom: '1px solid var(--border-subtle)',
  padding: '10px 0',
};

const cell: CSSProperties = {
  padding: '0 10px',
};

const shimmer: CSSProperties = {
  height: 14,
  width: '70%',
  borderRadius: 4,
  background:
    'linear-gradient(90deg, var(--shimmer-base) 0%, var(--shimmer-highlight) 50%, var(--shimmer-base) 100%)',
  backgroundSize: '200% 100%',
  animation: 'tableSkeletonShimmer 1.5s ease-in-out infinite',
};
